import { useMemo, useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { Boxes, Download, Search, Server, SlidersHorizontal } from 'lucide-react';
import { Container, PageHeader, Section, Toolbar, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  Input,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useCluster,
  useClusterPools,
  useClusterNodes,
  useClusterSnapshots,
  usePoolMachines,
  useDeletePool,
  useScalePool,
  useRecoverPool,
  useDrainNode,
  useRemoveNode,
  useCreateClusterSnapshot,
  useDeleteClusterSnapshot,
  useRestoreClusterSnapshot,
  fetchKubeconfig,
  nodeName,
  nodeReady,
  nodeRole,
  nodeIp,
  type K8sPool,
  type PoolMachine,
  type K8sNode,
  type ClusterSnapshot,
} from '@/api/k8s';
import { RowMenu } from '../compute/components/CrudBits';
import { PoolDetailDialog, PoolEditDialog, PoolCreateDialog } from './cluster-detail/PoolDialogs';
import { Overview } from './cluster-detail/ClusterOverview';
import { MetricsTab, IngressDetailTab, SnapshotScheduleCron } from './cluster-detail/ClusterMetricsIngress';
import { Instances } from './cluster-detail/ClusterInstances';
import { Logs } from './cluster-detail/ClusterLogs';
import { ClusterSettings } from './cluster-detail/ClusterSettings';

// Long values (name) truncate to a single line with an ellipsis so rows keep
// a dense, uniform height instead of wrapping onto 2+ ragged lines. Full
// value stays available via the cell `title` attribute.
const truncate = (max: number): React.CSSProperties => ({ display: 'block', maxWidth: max, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });

// Cluster sub-sections live in the app's ContextualSidebar (CLUSTER_NAV in
// KubernetesPage.tsx) — keep its slugs in sync with the routes below.

export function ClusterDetail() {
  const navigate = useNavigate();
  const { serviceId, clusterId } = useParams<{ serviceId: string; clusterId: string }>();
  const { show } = useToast();
  const { data: cluster, isLoading } = useCluster(serviceId, clusterId);
  const listPath = `/services/kubernetes/${serviceId}/clusters`;

  const downloadKubeconfig = async () => {
    try {
      const yamlText = await fetchKubeconfig(serviceId!, clusterId!);
      const blob = new Blob([yamlText], { type: 'text/yaml' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${cluster?.name || 'cluster'}-kubeconfig.yaml`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      show({ tone: 'danger', title: 'Could not fetch KubeConfig' });
    }
  };

  if (isLoading) {
    return <Container size="xl"><Skeleton style={{ height: 36, width: 260, marginBottom: 'var(--vn-space-4)' }} /><Skeleton style={{ height: 280 }} /></Container>;
  }
  if (!cluster) {
    return <Container size="xl"><EmptyState icon={<Boxes size={20} />} title="Cluster not found" description="It may have been deleted." actions={<Button variant="secondary" onClick={() => navigate(listPath)}>Back</Button>} /></Container>;
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Boxes size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem
              href={listPath}
              onClick={(e) => {
                e.preventDefault();
                navigate(listPath);
              }}
            >
              Clusters
            </BreadcrumbItem>
            <BreadcrumbItem current>{cluster.name || clusterId}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-3)', minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cluster.name || clusterId}
            </span>
            <StatusPill tone={cluster.status === 'active' ? 'success' : 'muted'}>{cluster.status ? cluster.status.charAt(0).toUpperCase() + cluster.status.slice(1) : cluster.state ? cluster.state.charAt(0).toUpperCase() + cluster.state.slice(1) : 'Unknown'}</StatusPill>
          </span>
        }
        actions={
          <Button variant="secondary" startIcon={<Download size={15} />} onClick={downloadKubeconfig}>Download KubeConfig</Button>
        }
      />
      <Routes>
        <Route index element={<Overview serviceId={serviceId!} clusterId={clusterId!} cluster={cluster} />} />
        <Route path="metrics" element={<MetricsTab serviceId={serviceId!} clusterId={clusterId!} />} />
        <Route path="pools" element={<Pools serviceId={serviceId!} clusterId={clusterId!} clusterUid={cluster.uid} />} />
        <Route path="nodes" element={<Nodes serviceId={serviceId!} clusterId={clusterId!} />} />
        <Route path="instances" element={<Instances serviceId={serviceId!} clusterId={clusterId!} />} />
        <Route path="ingress" element={<IngressDetailTab serviceId={serviceId!} clusterId={clusterId!} />} />
        <Route path="snapshots" element={<Snapshots serviceId={serviceId!} clusterId={clusterId!} />} />
        <Route path="logs" element={<Logs serviceId={serviceId!} clusterId={clusterId!} />} />
        <Route path="settings" element={<ClusterSettings serviceId={serviceId!} clusterId={clusterId!} cluster={cluster} />} />
        <Route path="*" element={<Overview serviceId={serviceId!} clusterId={clusterId!} cluster={cluster} />} />
      </Routes>
    </Container>
  );
}

// Backend maps pool states to display words: active→"Ready",
// resizing/provisioning/draft→"Updating", draining→"Draining". `poolScaling`
// flags an in-flight op (so the row shows current→target and the list polls).
const poolScaling = (p: K8sPool) => /updating|resizing|provision|pending/i.test(p.status ?? '');
const poolStatusTone = (s?: string): 'success' | 'info' | 'warning' | 'muted' => {
  const v = (s ?? '').toLowerCase();
  if (/ready|running|active/.test(v)) return 'success';
  if (/updating|resizing|provision|draft|pending/.test(v)) return 'info';
  if (/drain|terminat|delet/.test(v)) return 'warning';
  return 'muted';
};

// Columns toggleable via "Display options" in Pools — name/status/actions fixed.
const POOL_TOGGLEABLE = [
  { id: 'flavor', label: 'Flavor' },
  { id: 'scale', label: 'Scale' },
  { id: 'nodes', label: 'Nodes' },
];

function Pools({ serviceId, clusterId, clusterUid }: { serviceId: string; clusterId: string; clusterUid?: string }) {
  const { show } = useToast();
  const { data: pools = [], isLoading } = useClusterPools(serviceId, clusterId);
  const [createOpen, setCreateOpen] = useState(false);
  const [del, setDel] = useState<K8sPool | null>(null);
  const [scale, setScale] = useState<K8sPool | null>(null);
  const [detailFor, setDetailFor] = useState<K8sPool | null>(null);
  const [editFor, setEditFor] = useState<K8sPool | null>(null);
  const [machinesFor, setMachinesFor] = useState<K8sPool | null>(null);
  const [recoverFor, setRecoverFor] = useState<K8sPool | null>(null);
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState<Record<string, boolean>>({ flavor: true, scale: true, nodes: true });

  const remove = useDeletePool(serviceId, clusterId, { onSuccess: () => { show({ tone: 'success', title: 'Pool deleted' }); setDel(null); }, onError: () => show({ tone: 'danger', title: 'Delete failed' }) });
  const recover = useRecoverPool(serviceId, clusterId, { onSuccess: () => { show({ tone: 'success', title: 'Pool recovery started' }); setRecoverFor(null); }, onError: () => show({ tone: 'danger', title: 'Recover failed' }) });

  // The backend rejects deleting a non-worker pool or the cluster's last active
  // pool, so guard the action the same way before opening the confirm. List rows
  // may omit `roles`; an unknown role is treated as worker to avoid over-blocking.
  const readyPools = pools.filter((p) => /ready/i.test(String(p.status ?? ''))).length;
  const poolRole = (p: K8sPool) => (Array.isArray(p.roles) ? p.roles[0] : p.roles);
  const guardDeletePool = (p: K8sPool) => {
    const role = poolRole(p);
    if (role && role !== 'worker') { show({ tone: 'danger', title: 'Only worker pools can be deleted' }); return; }
    if (readyPools <= 1) { show({ tone: 'danger', title: 'Cannot delete the last remaining pool' }); return; }
    setDel(p);
  };

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => q ? pools.filter((p) => (p.name || String(p.id)).toLowerCase().includes(q)) : pools, [pools, q]);

  const columns: ColumnDef<K8sPool, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => { const n = row.original.name || String(row.original.id); return <span title={n} style={{ fontWeight: 'var(--vn-font-weight-medium)' as unknown as number, ...truncate(220) }}>{n}</span>; } },
    { id: 'flavor', header: 'Flavor', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.instanceType || row.original.metadata?.flavor || '—'}</span> },
    // Prefer actualMinSize/actualMaxSize: backend zeroes minSize/maxSize for manual pools.
    { id: 'scale', header: 'Scale', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.actualMinSize ?? row.original.minSize ?? row.original.min ?? '?'}–{row.original.actualMaxSize ?? row.original.maxSize ?? row.original.max ?? '?'}{row.original.autoscaling ? ' (auto)' : ''}</span> },
    // While a pool scales, show the live node count transitioning to its target
    // (current → desired) so the scale is observable, like the legacy console.
    { id: 'nodes', header: 'Nodes', cell: ({ row }) => {
      const p = row.original;
      const cur = p.currentSize ?? p.totalNodes;
      const des = p.desiredSize;
      return poolScaling(p) && des != null && des !== cur
        ? <span>{cur ?? '—'} <span style={{ color: 'var(--vn-fg-muted)' }}>→ {des}</span></span>
        : <>{cur ?? '—'}</>;
    } },
    { id: 'status', header: 'Status', cell: ({ row }) => { const s = row.original.status; return <StatusPill tone={poolStatusTone(s)}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : '—'}</StatusPill>; } },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[{ label: 'View detail', onSelect: () => setDetailFor(row.original) }, { label: 'Edit', onSelect: () => setEditFor(row.original) }, { label: 'Scale', onSelect: () => setScale(row.original) }, { label: 'View machines', onSelect: () => setMachinesFor(row.original) }, { label: 'Recover', onSelect: () => setRecoverFor(row.original) }, { label: 'Delete', danger: true, onSelect: () => guardDeletePool(row.original) }]} /> },
  ];
  // Hide toggled-off columns (name/status/actions always shown).
  const shownColumns = columns.filter((c) => (c.id && POOL_TOGGLEABLE.some((t) => t.id === c.id) ? visible[c.id] : true));

  return (
    <Section title={`Node pools${pools.length ? ` (${pools.length})` : ''}`} description="Worker pools that schedule your workloads." actions={<Button onClick={() => setCreateOpen(true)}>New pool</Button>}>
      {/* Empty placeholder renders inside the table's own bordered frame (via
          `emptyState`), so the zone keeps the same frame whether empty or full. */}
      {isLoading ? <Skeleton style={{ height: 200 }} /> : (
        <>
          <Toolbar
            start={<Input placeholder="Search pools by name" startSlot={<Search />} value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 320 }} />}
            end={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>Display options</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {POOL_TOGGLEABLE.map((c) => (
                    <DropdownMenuCheckboxItem
                      key={c.id}
                      checked={visible[c.id]}
                      onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {c.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
          <DataTable
            columns={shownColumns}
            data={filtered}
            getRowId={(p) => String(p.id)}
            emptyState={<EmptyState icon={<Boxes size={20} />} title="No node pools yet" description="Add a worker pool to schedule workloads." />}
            footer={`Showing ${filtered.length ? 1 : 0}–${filtered.length} of ${pools.length}`}
          />
        </>
      )}
      <PoolCreateDialog serviceId={serviceId} clusterId={clusterId} clusterUid={clusterUid} open={createOpen} onClose={() => setCreateOpen(false)} />
      {detailFor && <PoolDetailDialog serviceId={serviceId} clusterId={clusterId} pool={detailFor} onClose={() => setDetailFor(null)} />}
      {editFor && <PoolEditDialog serviceId={serviceId} clusterId={clusterId} pool={editFor} onClose={() => setEditFor(null)} />}
      {scale && <ScaleDialog serviceId={serviceId} clusterId={clusterId} pool={scale} onClose={() => setScale(null)} />}
      {machinesFor && <MachinesDialog serviceId={serviceId} clusterId={clusterId} pool={machinesFor} onClose={() => setMachinesFor(null)} />}
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete node pool?" description={`Remove "${del?.name || del?.id}" and drain its nodes?`} confirmLabel="Delete" destructive loading={remove.isPending} onConfirm={() => del && remove.mutate(del.id)} />
      <ConfirmDialog open={!!recoverFor} onOpenChange={(o) => !o && setRecoverFor(null)} title="Recover node pool?" description={`Attempt to recover "${recoverFor?.name || recoverFor?.id}" by re-reconciling its nodes.`} confirmLabel="Recover" loading={recover.isPending} onConfirm={() => recoverFor && recover.mutate(recoverFor.id)} />
    </Section>
  );
}

function ScaleDialog({ serviceId, clusterId, pool, onClose }: { serviceId: string; clusterId: string; pool: K8sPool; onClose: () => void }) {
  const { show } = useToast();
  const [desired, setDesired] = useState(
    String((pool as { desiredSize?: number }).desiredSize ?? pool.minSize ?? pool.min ?? 1),
  );
  const scale = useScalePool(serviceId, clusterId, pool.id, { onSuccess: () => { show({ tone: 'success', title: 'Pool scaling' }); onClose(); }, onError: () => show({ tone: 'danger', title: 'Scale failed' }) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Scale {pool.name || pool.id}</DialogTitle></DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          <Field label="Desired nodes">{(p) => <Input {...p} type="number" min={0} value={desired} onChange={(e) => setDesired(e.target.value)} />}</Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={scale.isPending} onClick={() => scale.mutate({ desiredSize: Number(desired) })}>Scale</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MachinesDialog({ serviceId, clusterId, pool, onClose }: { serviceId: string; clusterId: string; pool: K8sPool; onClose: () => void }) {
  const { show } = useToast();
  const { data: machines = [], isLoading } = usePoolMachines(serviceId, clusterId, pool.id);
  const [drainNode, setDrainNode] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const drain = useDrainNode(serviceId, clusterId, pool.id, { onSuccess: () => { show({ tone: 'success', title: 'Node draining' }); setDrainNode(null); }, onError: () => show({ tone: 'danger', title: 'Drain failed' }) });
  const removeNode = useRemoveNode(serviceId, clusterId, pool.id, { onSuccess: () => { show({ tone: 'success', title: 'Node removed' }); setRemoveTarget(null); }, onError: () => show({ tone: 'danger', title: 'Remove failed' }) });
  const columns: ColumnDef<PoolMachine, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as unknown as number }}>{row.original.name || row.original.id}</span> },
    // IP lives at metadata.ip on the payload (flat `ip` kept as fallback).
    { id: 'ip', header: 'IP', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.metadata?.ip || row.original.ip || '—'}</span> },
    // Real machine field is `state`; top-level `status` (e.g. "active") kept as fallback.
    { id: 'status', header: 'Status', cell: ({ row }) => { const s = row.original.state ?? row.original.status; const v = String(s ?? '').toLowerCase(); const label = s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : 'Unknown'; return <StatusPill tone={v.includes('run') || v.includes('active') || v.includes('on') ? 'success' : 'muted'}>{label}</StatusPill>; } },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[{ label: 'Drain', onSelect: () => setDrainNode(String(row.original.name ?? row.original.id)) }, { label: 'Remove', danger: true, onSelect: () => setRemoveTarget(String(row.original.name ?? row.original.id)) }]} /> },
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>Machines — {pool.name || pool.id}</DialogTitle></DialogHeader>
        <DialogBody>
          {isLoading ? <Skeleton style={{ height: 80 }} /> : machines.length === 0 ? (
            <EmptyState icon={<Server size={20} />} title="No machines yet" description="Machines appear once the pool is provisioned." />
          ) : (
            <DataTable columns={columns} data={machines} getRowId={(m) => String(m.id)} />
          )}
        </DialogBody>
        <DialogFooter><Button variant="secondary" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>

      <ConfirmDialog
        open={!!drainNode}
        onOpenChange={(o) => !o && setDrainNode(null)}
        title="Drain node?"
        description={`Cordon "${drainNode}" and evict its pods so they reschedule onto other nodes. The node stays in the pool and can be uncordoned later.`}
        confirmLabel="Drain"
        loading={drain.isPending}
        onConfirm={() => drainNode && drain.mutate(drainNode)}
      />
      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
        title="Remove node?"
        description={`This permanently removes "${removeTarget}" from the pool. Type the node name to confirm.`}
        confirmLabel="Remove"
        destructive
        requireText={removeTarget ?? undefined}
        loading={removeNode.isPending}
        onConfirm={() => removeTarget && removeNode.mutate(removeTarget)}
      />
    </Dialog>
  );
}

// Columns toggleable via "Display options" in Nodes — name/status fixed (no actions).
const NODE_TOGGLEABLE = [
  { id: 'role', label: 'Role' },
  { id: 'ip', label: 'Internal IP' },
];

function Nodes({ serviceId, clusterId }: { serviceId: string; clusterId: string }) {
  const { data, isLoading } = useClusterNodes(serviceId, clusterId);
  const nodes = data?.nodes ?? [];
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState<Record<string, boolean>>({ role: true, ip: true });

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => q ? nodes.filter((n) => nodeName(n).toLowerCase().includes(q)) : nodes, [nodes, q]);

  const columns: ColumnDef<K8sNode, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => { const n = nodeName(row.original); return <span title={n} style={{ fontWeight: 'var(--vn-font-weight-medium)' as unknown as number, ...truncate(260) }}>{n}</span>; } },
    { id: 'role', header: 'Role', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)', textTransform: 'capitalize' }}>{nodeRole(row.original)}</span> },
    { id: 'ip', header: 'Internal IP', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{nodeIp(row.original) || '—'}</span> },
    { id: 'status', header: 'Status', cell: ({ row }) => <StatusPill tone={nodeReady(row.original) ? 'success' : 'muted'}>{nodeReady(row.original) ? 'Ready' : 'Not ready'}</StatusPill> },
  ];
  // Hide toggled-off columns (name/status always shown).
  const shownColumns = columns.filter((c) => (c.id && NODE_TOGGLEABLE.some((t) => t.id === c.id) ? visible[c.id] : true));

  return (
    <Section title={`Nodes${nodes.length ? ` (${nodes.length})` : ''}`} description="Kubernetes nodes across all pools.">
      {/* Empty placeholder renders inside the table's own bordered frame (via
          `emptyState`), so the zone keeps the same frame whether empty or full. */}
      {isLoading ? <Skeleton style={{ height: 200 }} /> : (
        <>
          <Toolbar
            start={<Input placeholder="Search nodes by name" startSlot={<Search />} value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 320 }} />}
            end={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>Display options</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                  {NODE_TOGGLEABLE.map((c) => (
                    <DropdownMenuCheckboxItem
                      key={c.id}
                      checked={visible[c.id]}
                      onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {c.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
          <DataTable
            columns={shownColumns}
            data={filtered}
            getRowId={(n, i) => nodeName(n) || String(i)}
            emptyState={<EmptyState icon={<Boxes size={20} />} title="No nodes yet" description="Nodes appear once the cluster is ready." />}
            footer={`Showing ${filtered.length ? 1 : 0}–${filtered.length} of ${nodes.length}`}
          />
        </>
      )}
    </Section>
  );
}

// Columns toggleable via "Display options" in Snapshots — name/status/actions fixed.
const SNAP_TOGGLEABLE: { id: string; label: string }[] = [];

// Snapshot status tone: ready/done/complete/success → green; else muted.
// "Completed" was grey because the regex only matched `ready|done`; extend to
// cover `complete` and `success` so all finished states render as success tone.
const snapStatusTone = (s?: string): 'success' | 'muted' => {
  const v = String(s ?? '').toLowerCase();
  return /ready|done|complete|success/.test(v) ? 'success' : 'muted';
};

function Snapshots({ serviceId, clusterId }: { serviceId: string; clusterId: string }) {
  const { show } = useToast();
  const { data: snaps = [], isLoading } = useClusterSnapshots(serviceId, clusterId);
  const [create, setCreate] = useState(false);
  const [restore, setRestore] = useState<ClusterSnapshot | null>(null);
  const [del, setDel] = useState<ClusterSnapshot | null>(null);
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState<Record<string, boolean>>({});

  const createM = useCreateClusterSnapshot(serviceId, clusterId, { onSuccess: () => { show({ tone: 'success', title: 'Snapshot started' }); setCreate(false); }, onError: () => show({ tone: 'danger', title: 'Snapshot failed' }) });
  const restoreM = useRestoreClusterSnapshot(serviceId, clusterId, { onSuccess: () => { show({ tone: 'success', title: 'Restore started' }); setRestore(null); }, onError: () => show({ tone: 'danger', title: 'Restore failed' }) });
  const delM = useDeleteClusterSnapshot(serviceId, clusterId, { onSuccess: () => { show({ tone: 'success', title: 'Snapshot deleted' }); setDel(null); }, onError: () => show({ tone: 'danger', title: 'Delete failed' }) });

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => q ? snaps.filter((s) => (s.name || String(s.id)).toLowerCase().includes(q)) : snaps, [snaps, q]);

  const columns: ColumnDef<ClusterSnapshot, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => { const n = row.original.name || String(row.original.id); return <span title={n} style={{ fontWeight: 'var(--vn-font-weight-medium)' as unknown as number, ...truncate(260) }}>{n}</span>; } },
    { id: 'status', header: 'Status', cell: ({ row }) => { const s = row.original.status; const label = s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown'; return <StatusPill tone={snapStatusTone(s)}>{label}</StatusPill>; } },
    { id: 'actions', header: '', cell: ({ row }) => <RowMenu items={[{ label: 'Restore', onSelect: () => setRestore(row.original) }, { label: 'Delete', danger: true, onSelect: () => setDel(row.original) }]} /> },
  ];
  // Hide toggled-off columns — for snapshots all non-fixed columns are shown by default.
  const shownColumns = columns.filter((c) => (c.id && SNAP_TOGGLEABLE.some((t) => t.id === c.id) ? visible[c.id] : true));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <SnapshotScheduleCron serviceId={serviceId} clusterId={clusterId} />
    <Section title={`Snapshots${snaps.length ? ` (${snaps.length})` : ''}`} description="Point-in-time cluster state you can restore." style={{ marginBottom: 0 }} actions={<Button disabled={createM.isPending} onClick={() => setCreate(true)}>Take snapshot</Button>}>
      {/* Empty placeholder renders inside the table's own bordered frame (via
          `emptyState`), so the zone keeps the same frame whether empty or full. */}
      {isLoading ? <Skeleton style={{ height: 200 }} /> : (
        <>
          {/* Snapshots has no toggleable columns, so no "Display options" button —
              an empty column menu is pointless chrome. */}
          <Toolbar
            start={<Input placeholder="Search snapshots by name" startSlot={<Search />} value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 320 }} />}
            end={
              SNAP_TOGGLEABLE.length > 0 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>Display options</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                    {SNAP_TOGGLEABLE.map((c) => (
                      <DropdownMenuCheckboxItem
                        key={c.id}
                        checked={visible[c.id]}
                        onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {c.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : undefined
            }
          />
          <DataTable
            columns={shownColumns}
            data={filtered}
            getRowId={(s) => String(s.id)}
            emptyState={<EmptyState icon={<Boxes size={20} />} title="No snapshots yet" description="Snapshot the cluster state to restore later." />}
            footer={`Showing ${filtered.length ? 1 : 0}–${filtered.length} of ${snaps.length}`}
          />
        </>
      )}
      <ConfirmDialog open={create} onOpenChange={setCreate} title="Take cluster snapshot?" description="Capture the current cluster state." confirmLabel="Take snapshot" loading={createM.isPending} onConfirm={() => createM.mutate(undefined)} />
      <ConfirmDialog open={!!restore} onOpenChange={(o) => !o && setRestore(null)} title="Restore snapshot?" description="The cluster will roll back to this snapshot." confirmLabel="Restore" destructive loading={restoreM.isPending} onConfirm={() => restore && restoreM.mutate(restore.id)} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete snapshot?" description="This permanently removes the snapshot." confirmLabel="Delete" destructive loading={delM.isPending} onConfirm={() => del && delM.mutate(del.id)} />
    </Section>
    </div>
  );
}

